<template>
  <div
    data-testid="license-info-container"
    class="wrapper shadowboxing"
  >
    <div
      data-testid="license-info--main-title"
      class="main-label ibf-h2"
    >
      {{ $t('license.title') }}
      <span
        v-if="isLicenseOffline"
        class="ibf-gordita-12-m dot-divider"
      >
        <q-icon
          class="dotIcon"
          size="12px"
          name="fiber_manual_record"
        ></q-icon>
        OFFLINE
      </span>
    </div>

    <div class="details">
      <div
        class="license-key"
        tabindex="0"
        @mouseenter="showCopy = true"
        @mouseleave="showCopy = false"
      >
        <div
          v-if="licenseKey"
          :data-testid="elementIds.license.licenseKey"
          class="ib-color-text-grey gordita-400-11"
        >
          {{ licenseKey }}
        </div>
        <q-btn
          data-testid="license-info--copy-button"
          v-show="showCopy"
          flat
          round
          class="ib-color-dark-green copy-button"
          icon="icon-copy"
          @click="copyClipboard()"
        ></q-btn>
      </div>

      <div class="last-updated ib-color-almost_black ibf-text-body1">
        <div v-if="!isLicenseOffline">
          <div class="gordita-400-12 app_text_grey">
            <span
              data-testid="license-info--last-update-title"
              style="margin-right: 4px"
              >Last update</span
            >
            <ib-tooltip
              data-testid="license-info--last-update-tooltip"
              tip-text="Our servers check to see if there have been any license changes a few times per day. If you made updates after this time, it won't be reflected in your account yet."
            ></ib-tooltip>
          </div>
          <div
            class="gordita-400-12 date-info ibf-text-body1"
            style="padding-bottom: 4px"
          >
            <div
              data-testid="license-info--last-update-date"
              class="gordita-medium-500-13"
            >
              <span>{{ `${dateUtils.getFormattedDate(lastUpdateTime)} | ${dateUtils.getFormattedTime(lastUpdateTime)}` }} </span>
            </div>
          </div>
          <refresh-license :license-key="licenseKey"></refresh-license>
        </div>
        <div
          v-if="!isLicenseTypeFree"
          class="ib-color-almost_black ibf-text-body1"
          style="padding-top: 16px"
        >
          <div
            data-testid="license-info--expiration-date-title"
            class="gordita-400-12 app_text_grey"
          >
            Expiration Date
          </div>
          <span
            data-testid="license-info--expiration-date-value"
            class="gordita-medium-500-13"
            >{{ dateUtils.getFormattedDate(expirationDaysLeftDayBefore) }}
          </span>
        </div>
        <div
          v-if="showRenewalNote"
          class="mt-4 bottom-5 absolute w-[204px]"
        >
          <incredi-note
            type="Information"
            :show-note="false"
          >
            {{ $t('license.renewal') }}
          </incredi-note>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { copyToClipboard } from 'quasar';
import { IncrediNote } from 'incredi-ui';

import IbTooltip from '@/components/common/Tooltip.vue';
import elementIds from '@/const/elementIds.json';
import { dateUtils } from '@/services/dateUtils';
import { useMatomo } from '@/services/matomo/matomo.service';
import RefreshLicense from '../../licenserComponents/refreshLicense.vue';

export default {
  name: 'LicenseInfoWindows',
  components: { RefreshLicense, IbTooltip, IncrediNote },
  props: {
    lastUpdateTime: { required: true },
    licenseKey: { type: String },
    expirationDaysLeft: { type: Number },
    contractEndDate: { type: Number },
    isLicenseOffline: { type: Boolean },
    isLicenseTypeFree: { type: Boolean },
  },
  setup() {
    const matomo = useMatomo();

    return {
      matomo,
      elementIds,
      dateUtils,
    };
  },
  data() {
    return {
      showCopy: false,
    };
  },
  computed: {
    expirationDaysLeftDayBefore() {
      // Subtract 1 day - handle different timezones (WIN-14950)
      return moment(this.expirationDaysLeft).subtract(1, 'days');
    },
    showRenewalNote() {
      if (this.isLicenseOffline) {
        return false;
      }
      return this.contractEndDate && moment(this.contractEndDate).isAfter(moment(this.expirationDaysLeft).add(3, 'days'));
    },
  },
  methods: {
    copyClipboard() {
      copyToClipboard(this.licenseKey);
      this.matomo?.trackEvent({
        category: 'License Page',
        action: 'Copy License',
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.dot-divider {
  margin-left: 45px;
  color: $-text-text-low-old;
}
.dotIcon {
  color: $-system-app-grey-76 !important;
  padding-right: 3px;
  margin-bottom: 3px;
}
.wrapper {
  flex-grow: 1;
  width: 100%;
  min-height: 372px;
  margin: 24px 0 0;
  padding: 20px;
  position: relative;

  & .main-label {
    margin-bottom: 12px;
  }

  & .second-label {
    margin-bottom: 24px;
  }

  & .license-key {
    background-color: $-system-app-background;
    border-radius: 2px;
    width: 100%;
    height: 37px;
    line-height: 37px;
    vertical-align: middle;
    padding: 0 10px;
    color: $-text-text-low-old;
    display: flex;
    justify-content: space-between;

    & .copy-button {
      margin-right: 4px;
      margin-top: 2px;
      font-size: 11px;
      position: absolute;
      right: 20px;
    }
  }
  .details {
    height: calc(100% - 42px);
  }
}

.last-updated {
  margin-top: 24px;
}
</style>
